'use client';

import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Stack
} from '@mui/material';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { KeyRound, Eye, EyeOff } from 'lucide-react';
import { useSnackbar } from 'components/snackbar/snackbarProvider';
import { usersApi } from 'features/users/services/usersApi ';

export default function ResetPasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setErrors({});
    }
  }, [open]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.currentPassword) errs.currentPassword = 'Current password is required';
    if (!form.newPassword) errs.newPassword = 'New password is required';
    else if (form.newPassword.length < 8) errs.newPassword = 'At least 8 characters required';
    if (!form.confirmPassword) errs.confirmPassword = 'Please confirm your password';
    else if (form.newPassword !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    try {
      setIsLoading(true);
      await usersApi.resetPassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      console.log('API CALL:', { currentPassword: form.currentPassword, newPassword: form.newPassword });
      showSnackbar('Password updated successfully', 'success');
      onClose();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to reset password';
      showSnackbar(msg, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const checks = [
    { label: 'At least 8 characters', pass: form.newPassword.length >= 8 },
    { label: 'One uppercase letter', pass: /[A-Z]/.test(form.newPassword) },
    { label: 'One number or symbol', pass: /[0-9!@#$%^&*]/.test(form.newPassword) }
  ];

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      fontSize: '14px',
      '&:hover fieldset': { borderColor: '#6366f1' },
      '&.Mui-focused fieldset': { borderColor: '#6366f1' }
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#6366f1' }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          border: '1px solid #e5e7eb'
        }
      }}
    >
      {/* Header — same pattern as CreateUserDialog */}
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '20px',
          py: '14px',
          borderBottom: '1px solid #f1f5f9',
          flexShrink: 0,
          background: '#ffffff'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <KeyRound size={17} color="#ffffff" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>Reset Password</Typography>
            <Typography sx={{ fontSize: '12px', color: '#94a3b8', lineHeight: 1.2, mt: '2px' }}>Update your account password</Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: '#94a3b8', borderRadius: '8px', p: '6px', '&:hover': { color: '#64748b', background: '#f1f5f9' } }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* Body */}
      <DialogContent sx={{ px: '20px', pt: '24px', pb: '12px', overflowY: 'auto' }}>
        <Stack spacing={2.5} sx={{ mt: 2 }}>
          <TextField
            label="Current Password *"
            type={showCurrent ? 'text' : 'password'}
            fullWidth
            size="small"
            value={form.currentPassword}
            onChange={handleChange('currentPassword')}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowCurrent((v) => !v)} edge="end">
                    {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            label="New Password *"
            type={showNew ? 'text' : 'password'}
            fullWidth
            size="small"
            value={form.newPassword}
            onChange={handleChange('newPassword')}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowNew((v) => !v)} edge="end">
                    {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            label="Confirm New Password *"
            type={showConfirm ? 'text' : 'password'}
            fullWidth
            size="small"
            value={form.confirmPassword}
            onChange={handleChange('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowConfirm((v) => !v)} edge="end">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {/* Strength checklist */}
          <Box sx={{ background: '#f8fafc', borderRadius: '8px', p: '12px', border: '1px solid #f1f5f9' }}>
            <Typography
              sx={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', mb: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              Password requirements
            </Typography>
            <Stack spacing={0.75}>
              {checks.map(({ label, pass }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: pass ? '#dcfce7' : '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: pass ? '#16a34a' : '#cbd5e1' }} />
                  </Box>
                  <Typography sx={{ fontSize: '12px', color: pass ? '#16a34a' : '#94a3b8' }}>{label}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      {/* Footer — same style as UserForm buttons */}
      <DialogActions
        sx={{
          px: '20px',
          py: '14px',
          borderTop: '1px solid #f1f5f9',
          flexShrink: 0,
          background: '#ffffff',
          gap: 1
        }}
      >
        <Button
          onClick={onClose}
          fullWidth
          variant="outlined"
          sx={{
            borderRadius: '8px',
            borderColor: '#e2e8f0',
            color: '#64748b',
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'none',
            py: '8px',
            '&:hover': { borderColor: '#cbd5e1', background: '#f8fafc' }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          fullWidth
          variant="contained"
          disabled={isLoading}
          sx={{
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'none',
            py: '8px',
            boxShadow: 'none',
            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', boxShadow: 'none' },
            '&:disabled': { opacity: 0.6 }
          }}
        >
          {isLoading ? 'Updating...' : 'Update Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
